import { API_VERSION, CharacterType, GetGroupchatsParams, ICharacter } from "@next-platform/client-common-core";

import { WHITE_LABEL_API_VERSION } from "@/constants";
import jsonApiService from "@/services/jsonApiService";

export type ChatbotApiItem = Partial<ICharacter> & Pick<ICharacter, "name" | "title" | "greeting">;

type ChatbotApiResponse = {
  ["data"]?: ChatbotApiItem[];
  lastKey?: string;
};

export const CHATBOTS_PAGE_LIMIT = 50;

export type GroupchatServerParams = {
  sort?: string;
  search?: string;
  filter?: string;
};

export type FetchGroupchatsParams = GroupchatServerParams & {
  lastKey?: string;
  isLoadMore?: boolean;
};

export type ChatbotsStateSetters = {
  setStateChatbots: (updater: ICharacter[] | ((previous: ICharacter[] | null) => ICharacter[])) => void;
  setStateChatbotsLastKey: (lastKey: string | null) => void;
  setStateError: (error: Error | null) => void;
  setStateIsLoading: (isLoading: boolean) => void;
  setStateIsLoadingMoreGroupchats: (isLoading: boolean) => void;
};

type GetGroupchatsDataResult = {
  ["data"]: ICharacter[] | null;
  error: string | null;
  lastKey?: string;
  errorPayload?: unknown;
};

type GetGroupchatsData = (params?: GetGroupchatsParams) => Promise<GetGroupchatsDataResult>;

export const setFetchLoading = ({
  isLoadMore,
  isLoading,
  setStateIsLoading,
  setStateIsLoadingMoreGroupchats,
}: {
  isLoadMore?: boolean;
  isLoading: boolean;
  setStateIsLoading: (isLoading: boolean) => void;
  setStateIsLoadingMoreGroupchats: (isLoading: boolean) => void;
}): void => {
  if (isLoadMore) {
    setStateIsLoadingMoreGroupchats(isLoading);
    return;
  }
  setStateIsLoading(isLoading);
};

export const appendOrReplaceChatbots = ({
  isLoadMore,
  items,
  setStateChatbots,
}: {
  isLoadMore?: boolean;
  items: ICharacter[];
  setStateChatbots: ChatbotsStateSetters["setStateChatbots"];
}): void => {
  if (isLoadMore) {
    setStateChatbots((previous) => (previous ? [...previous, ...items] : items));
    return;
  }
  setStateChatbots(items);
};

const formatModerationReport = (moderationReport: ChatbotApiItem["moderationReport"]): ICharacter["moderationReport"] => {
  if (!moderationReport) return undefined;
  return {
    moderation_status: moderationReport.moderation_status,
    action_taken_description: "",
    reason_for_moderation: [],
  };
};

const formatIdentityFields = (chatItem: ChatbotApiItem): Pick<ICharacter, "id" | "character_id" | "name" | "title" | "greeting"> => ({
  id: chatItem.id || chatItem.character_id || "",
  character_id: chatItem.character_id || chatItem.id || "",
  name: chatItem.name || "",
  title: chatItem.title || "",
  greeting: chatItem.greeting || "",
});

const formatCreatorFields = (
  chatItem: ChatbotApiItem,
): Pick<ICharacter, "creator_user_id" | "creator_username" | "avatar_url" | "avatar_is_nsfw"> => ({
  creator_user_id: chatItem.creator_user_id || "",
  creator_username: chatItem.creator_username || "",
  avatar_url: chatItem.avatar_url || "",
  avatar_is_nsfw: chatItem.avatar_is_nsfw || false,
});

const formatMetaFields = (
  chatItem: ChatbotApiItem,
): Pick<
  ICharacter,
  | "definition_visible"
  | "is_nsfw"
  | "num_messages"
  | "token_count"
  | "visibility"
  | "updatedAt"
  | "createdAt"
  | "aicq_manual_review_required"
  | "rating_score"
  | "lora_status"
  | "tags"
  | "reportsType"
  | "name_i18n"
  | "title_i18n"
  | "type"
  | "sub_characters_count"
  | "has_lorebooks"
  | "voice_id"
> => ({
  definition_visible: chatItem.definition_visible || false,
  is_nsfw: chatItem.is_nsfw || false,
  num_messages: chatItem.num_messages || 0,
  token_count: chatItem.token_count || 0,
  visibility: (chatItem.visibility as ICharacter["visibility"]) || ("private" as ICharacter["visibility"]),
  updatedAt: chatItem.updatedAt || Date.now(),
  createdAt: chatItem.createdAt || Date.now(),
  aicq_manual_review_required: chatItem.aicq_manual_review_required || false,
  rating_score: chatItem.rating_score,
  lora_status: chatItem.lora_status,
  tags: chatItem.tags || [],
  reportsType: [],
  name_i18n: chatItem.name_i18n,
  title_i18n: chatItem.title_i18n,
  type: chatItem.type,
  sub_characters_count: chatItem.sub_characters_count ?? 0,
  has_lorebooks: chatItem.has_lorebooks || false,
  voice_id: chatItem.voice_id || "",
});

export const formatChatbotApiItem = ({
  chatItem,
  includeAttachedLorebookId,
}: {
  chatItem: ChatbotApiItem;
  includeAttachedLorebookId?: boolean;
}): ICharacter => ({
  ...formatIdentityFields(chatItem),
  ...formatCreatorFields(chatItem),
  ...formatMetaFields(chatItem),
  ...(includeAttachedLorebookId ? { attachedLorebookId: chatItem.attachedLorebookId } : {}),
  ...(chatItem.rulebook_id !== undefined ? { rulebook_id: chatItem.rulebook_id } : {}),
  ...(chatItem.has_rulebook !== undefined ? { has_rulebook: chatItem.has_rulebook } : {}),
  moderationReport: formatModerationReport(chatItem.moderationReport),
  ...(chatItem.moderationReportStatus !== undefined ? { moderationReportStatus: chatItem.moderationReportStatus } : {}),
});

export const buildStandardChatbotsUrl = ({
  type,
  params,
  includeAttachedLorebookId,
}: {
  type: CharacterType;
  params: FetchGroupchatsParams;
  includeAttachedLorebookId?: boolean;
}): string => {
  const urlParams = new URLSearchParams();
  urlParams.set("switch", "T1");
  urlParams.set("type", type);
  urlParams.set("limit", String(CHATBOTS_PAGE_LIMIT));
  if (params.lastKey) urlParams.set("lastKey", params.lastKey);
  if (params.sort) urlParams.set("sort", params.sort);
  if (params.search) urlParams.set("search", params.search);
  if (params.filter) urlParams.set("filter", params.filter);
  if (includeAttachedLorebookId) urlParams.set("includeAttachedLorebookId", "true");
  return `users/characters?${urlParams.toString()}`;
};

const isCurrentFetch = ({ fetchId, refFetchId }: { fetchId: number; refFetchId: { current: number } }): boolean => fetchId === refFetchId.current;

const handleFetchFailure = ({
  fetchId,
  isLoadMore,
  refFetchId,
  requestError,
  setStateChatbots,
  setStateError,
}: {
  fetchId: number;
  isLoadMore?: boolean;
  refFetchId: { current: number };
  requestError: unknown;
  setStateChatbots: ChatbotsStateSetters["setStateChatbots"];
  setStateError: ChatbotsStateSetters["setStateError"];
}): void => {
  if (!isCurrentFetch({ fetchId, refFetchId })) return;
  if (!isLoadMore) setStateChatbots([]);
  setStateError(requestError as Error);
};

const finishFetchLoading = ({
  fetchId,
  isLoadMore,
  refFetchId,
  setStateIsLoading,
  setStateIsLoadingMoreGroupchats,
}: {
  fetchId: number;
  isLoadMore?: boolean;
  refFetchId: { current: number };
  setStateIsLoading: ChatbotsStateSetters["setStateIsLoading"];
  setStateIsLoadingMoreGroupchats: ChatbotsStateSetters["setStateIsLoadingMoreGroupchats"];
}): void => {
  if (!isCurrentFetch({ fetchId, refFetchId })) return;
  setFetchLoading({
    isLoadMore,
    isLoading: false,
    setStateIsLoading,
    setStateIsLoadingMoreGroupchats,
  });
};

export const fetchGroupchatsRequest = async ({
  fetchId,
  getGroupchatsData,
  includeAttachedLorebookId,
  params,
  refFetchId,
  setStateChatbots,
  setStateChatbotsLastKey,
  setStateError,
  setStateIsLoading,
  setStateIsLoadingMoreGroupchats,
}: {
  fetchId: number;
  getGroupchatsData: GetGroupchatsData;
  includeAttachedLorebookId?: boolean;
  params: FetchGroupchatsParams;
  refFetchId: { current: number };
} & ChatbotsStateSetters): Promise<void> => {
  setFetchLoading({
    isLoadMore: params.isLoadMore,
    isLoading: true,
    setStateIsLoading,
    setStateIsLoadingMoreGroupchats,
  });
  setStateError(null);

  try {
    const response = await getGroupchatsData({
      lastKey: params.lastKey,
      sort: params.sort,
      search: params.search,
      filter: params.filter,
      ...(includeAttachedLorebookId ? { includeAttachedLorebookId: true } : {}),
    });

    if (!isCurrentFetch({ fetchId, refFetchId })) return;

    const responsePayload = response?.["data"];
    const fetchedGroupchats = Array.isArray(responsePayload) ? responsePayload : [];
    const responseError = !responsePayload && response?.error ? new Error(response.error) : null;

    appendOrReplaceChatbots({
      isLoadMore: params.isLoadMore,
      items: fetchedGroupchats,
      setStateChatbots,
    });
    setStateChatbotsLastKey(response?.lastKey ?? null);
    setStateError(responseError);
  } catch (requestError) {
    handleFetchFailure({
      fetchId,
      isLoadMore: params.isLoadMore,
      refFetchId,
      requestError,
      setStateChatbots,
      setStateError,
    });
  } finally {
    finishFetchLoading({
      fetchId,
      isLoadMore: params.isLoadMore,
      refFetchId,
      setStateIsLoading,
      setStateIsLoadingMoreGroupchats,
    });
  }
};

export const fetchStandardChatbotsRequest = async ({
  fetchId,
  includeAttachedLorebookId,
  params,
  refFetchId,
  setStateChatbots,
  setStateChatbotsLastKey,
  setStateError,
  setStateIsLoading,
  setStateIsLoadingMoreGroupchats,
  type,
}: {
  fetchId: number;
  includeAttachedLorebookId?: boolean;
  params: FetchGroupchatsParams;
  refFetchId: { current: number };
  type: CharacterType;
} & ChatbotsStateSetters): Promise<void> => {
  setFetchLoading({
    isLoadMore: params.isLoadMore,
    isLoading: true,
    setStateIsLoading,
    setStateIsLoadingMoreGroupchats,
  });
  setStateError(null);

  try {
    const url = buildStandardChatbotsUrl({ type, params, includeAttachedLorebookId });
    const resourceData = (await jsonApiService.fetchResource(url, null, {}, WHITE_LABEL_API_VERSION as API_VERSION)) as ChatbotApiResponse;

    if (!isCurrentFetch({ fetchId, refFetchId })) return;

    const responsePayload = resourceData?.["data"];
    const apiItems = Array.isArray(responsePayload) ? responsePayload : [];
    const formattedChatbots = apiItems.map((chatItem) => formatChatbotApiItem({ chatItem, includeAttachedLorebookId }));

    appendOrReplaceChatbots({
      isLoadMore: params.isLoadMore,
      items: formattedChatbots,
      setStateChatbots,
    });

    const hasNextPage = apiItems.length >= CHATBOTS_PAGE_LIMIT && resourceData?.lastKey != null;
    setStateChatbotsLastKey(hasNextPage ? JSON.stringify(resourceData.lastKey) : null);
  } catch (requestError) {
    handleFetchFailure({
      fetchId,
      isLoadMore: params.isLoadMore,
      refFetchId,
      requestError,
      setStateChatbots,
      setStateError,
    });
  } finally {
    finishFetchLoading({
      fetchId,
      isLoadMore: params.isLoadMore,
      refFetchId,
      setStateIsLoading,
      setStateIsLoadingMoreGroupchats,
    });
  }
};
